(() => {
  const sliderSelector = '.fwn-slider[data-fwn-slider-target]';
  const initialized = 'fwnSliderInitialized';

  const findTarget = (slider) => {
    const selector = slider.dataset.fwnSliderTarget;
    if (!selector) return null;

    let scope = slider.parentElement;
    while (scope) {
      const target = scope.matches?.(selector) ? scope : scope.querySelector?.(selector);
      if (target) return target;
      scope = scope.parentElement;
    }

    return null;
  };

  const getStartOffset = (slider, sliderWidth, maximumOffset) => {
    if (slider.dataset.fwnSliderStartOffset === 'edge') return 0;

    const preferredOffset = Math.min(Math.max(sliderWidth * 0.04, 8), 40);
    return Math.min(preferredOffset, maximumOffset);
  };

  const setPosition = (slider, scrollLeft = 0, maximumScroll = 0) => {
    const handle = slider?.querySelector('.fwn-slider__handle');
    if (!slider || !handle) return;

    const sliderWidth = slider.clientWidth;
    const handleWidth = Math.min(handle.getBoundingClientRect().width, sliderWidth);
    const maximumOffset = Math.max(sliderWidth - handleWidth, 0);
    const startOffset = getStartOffset(slider, sliderWidth, maximumOffset);
    const availableOffset = Math.max(maximumOffset - startOffset, 0);
    const scrollProgress = maximumScroll > 1
      ? Math.min(Math.max(scrollLeft / maximumScroll, 0), 1)
      : 0;
    const handleOffset = startOffset + scrollProgress * availableOffset;

    slider.style.setProperty('--fwn-slider-handle-offset', `${handleOffset}px`);
  };

  const initSlider = (slider) => {
    if (!slider || slider.dataset[initialized] === 'true') return;

    const handle = slider.querySelector('.fwn-slider__handle');
    const target = findTarget(slider);
    if (!handle || !target) return;

    slider.dataset[initialized] = 'true';
    target.classList.add('has-fwn-slider');
    setPosition(slider, target.scrollLeft, target.scrollWidth - target.clientWidth);

    let pointerId = null;
    let pointerOffset = 0;
    let pendingScrollLeft = 0;
    let frame = 0;

    const applyPendingScroll = () => {
      frame = 0;
      target.scrollLeft = pendingScrollLeft;
    };

    const setScrollFromPointer = (clientX) => {
      const sliderRect = slider.getBoundingClientRect();
      const handleWidth = Math.min(handle.getBoundingClientRect().width, sliderRect.width);
      const maximumOffset = Math.max(sliderRect.width - handleWidth, 0);
      const startOffset = getStartOffset(slider, sliderRect.width, maximumOffset);
      const availableOffset = Math.max(maximumOffset - startOffset, 0);
      const maximumScroll = Math.max(target.scrollWidth - target.clientWidth, 0);

      if (availableOffset <= 0 || maximumScroll <= 1) return;

      const handleOffset = Math.min(
        Math.max(clientX - sliderRect.left - pointerOffset, startOffset),
        maximumOffset
      );

      pendingScrollLeft = ((handleOffset - startOffset) / availableOffset) * maximumScroll;
      slider.style.setProperty('--fwn-slider-handle-offset', `${handleOffset}px`);

      if (!frame) frame = window.requestAnimationFrame(applyPendingScroll);
    };

    const handlePointerDown = (event) => {
      if (pointerId !== null || event.isPrimary === false) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (target.scrollWidth - target.clientWidth <= 1) return;

      const handleRect = handle.getBoundingClientRect();
      const pressedHandle = event.target === handle || handle.contains(event.target);

      if (event.cancelable) event.preventDefault();
      pointerId = event.pointerId;
      pointerOffset = pressedHandle ? event.clientX - handleRect.left : handleRect.width / 2;
      slider.classList.add('is-dragging');
      target.classList.add('is-fwn-slider-dragging');
      slider.setPointerCapture?.(event.pointerId);
      setScrollFromPointer(event.clientX);
    };

    const handlePointerMove = (event) => {
      if (event.pointerId !== pointerId) return;
      if (event.cancelable) event.preventDefault();
      setScrollFromPointer(event.clientX);
    };

    const stopDragging = (event) => {
      if (event.pointerId !== pointerId) return;

      if (slider.hasPointerCapture?.(event.pointerId)) {
        slider.releasePointerCapture(event.pointerId);
      }

      slider.classList.remove('is-dragging');
      target.classList.remove('is-fwn-slider-dragging');
      pointerId = null;

      if (frame) {
        window.cancelAnimationFrame(frame);
        applyPendingScroll();
      }
    };

    slider.addEventListener('pointerdown', handlePointerDown);
    slider.addEventListener('pointermove', handlePointerMove);
    slider.addEventListener('pointerup', stopDragging);
    slider.addEventListener('pointercancel', stopDragging);
    slider.addEventListener('lostpointercapture', stopDragging);

    slider.fwnSliderCleanup = () => {
      if (frame) window.cancelAnimationFrame(frame);
      slider.removeEventListener('pointerdown', handlePointerDown);
      slider.removeEventListener('pointermove', handlePointerMove);
      slider.removeEventListener('pointerup', stopDragging);
      slider.removeEventListener('pointercancel', stopDragging);
      slider.removeEventListener('lostpointercapture', stopDragging);
      slider.classList.remove('is-dragging');
      target.classList.remove('is-fwn-slider-dragging');
      target.classList.remove('has-fwn-slider');
      delete slider.dataset[initialized];
      delete slider.fwnSliderCleanup;
    };
  };

  const init = (root = document) => {
    if (root.matches?.(sliderSelector)) initSlider(root);
    root.querySelectorAll?.(sliderSelector).forEach(initSlider);
  };

  window.FWNSliders = { getStartOffset, init, setPosition };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(sliderSelector).forEach((slider) => slider.fwnSliderCleanup?.());
  });
})();
